import type { ReactNode } from 'react';
import { CHORE_DONE_NOTE_MAX, CHORE_REACTION_GLYPHS } from '../../../../../shared/types';
import type { Session } from '../../../shell/bridge';
import { Pressable } from '../../../ui/Pressable';
import { noteByLabel } from './helpers';
import type { ChoreItem } from './types';

/** The six reaction chips draw Lucide vectors now, not emoji (card bFaRParNQH98,
 *  Jonathan 2026-10-23: emoji render per-platform and sit outside the vector set
 *  the rest of the app draws). Shapes are inlined from lucide-static v1.61.0 with
 *  no icon dependency — the same reason Thread inlines sparkles / tic-tac-toe:
 *  a package would mix stroke weights with the shapes already in the tree.
 *
 *  The WIRE is untouched: CHORE_REACTION_GLYPHS stays emoji and
 *  POST /api/chores/:id/react still sends the glyph, so every reaction already
 *  stored keeps rendering. What leaves the DOM is the emoji itself — the chip
 *  carries `data-reaction` and a spoken word instead, which is what the walk
 *  asserts and what screen readers read. (lucide has no open-mouth-only face at
 *  1.61.0, so the 😮 slot draws `laugh`, the nearest open-mouth shape.) */
export const REACTION_SHAPES: Record<string, { name: string; label: string; body: ReactNode }> = {
  '👏': {
    name: 'clap',
    label: 'clap',
    body: (
      <>
        <path d="M18 11V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2" />
        <path d="M14 10V4a2 2 0 0 0-2-2a2 2 0 0 0-2 2v2" />
        <path d="M10 10.5V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2v8" />
        <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
      </>
    ),
  },
  '❤️': {
    name: 'heart',
    label: 'heart',
    body: (
      <path d="M2 9.5a5.5 5.5 0 0 1 9.591-3.676.56.56 0 0 0 .818 0A5.49 5.49 0 0 1 22 9.5c0 2.29-1.5 4-3 5.5l-5.492 5.313a2 2 0 0 1-3 .019L5 15c-1.5-1.5-3-3.2-3-5.5" />
    ),
  },
  '🙌': {
    name: 'hands',
    label: 'raised hands',
    body: (
      <>
        <path d="M11 14h2a2 2 0 0 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 16" />
        <path d="m14.45 13.39 5.05-4.694C20.196 8 21 6.85 21 5.75a2.75 2.75 0 0 0-4.797-1.837.276.276 0 0 1-.406 0A2.75 2.75 0 0 0 11 5.75c0 1.2.802 2.248 1.5 2.946L16 11.95" />
        <path d="m2 15 6 6" />
        <path d="m7 20 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a1 1 0 0 0-2.75-2.91" />
      </>
    ),
  },
  '🔥': {
    name: 'fire',
    label: 'fire',
    body: (
      <path d="M12 3q1 4 4 6.5t3 5.5a1 1 0 0 1-14 0 5 5 0 0 1 1-3 1 1 0 0 0 5 0c0-2-1.5-3-1.5-5q0-2 2.5-4" />
    ),
  },
  '😮': {
    name: 'wow',
    label: 'wow',
    body: (
      <>
        <path d="M15 10V9" />
        <path d="M7.084 14.302a5.12 5.12 0 0 0 9.833 0 .24.24 0 0 0-.235-.302H7.32a.24.24 0 0 0-.235.302" />
        <path d="M9 10V9" />
        <circle cx="12" cy="12" r="10" />
      </>
    ),
  },
  '🎉': {
    name: 'party',
    label: 'party',
    body: (
      <>
        <path d="M5.8 11.3 2 22l10.7-3.79" />
        <path d="M4 3h.01" />
        <path d="M22 8h.01" />
        <path d="M15 2h.01" />
        <path d="M22 20h.01" />
        <path d="m22 2-2.24.75a2.9 2.9 0 0 0-1.96 3.12c.1.86-.57 1.63-1.45 1.63h-.38c-.86 0-1.6.6-1.76 1.44L14 10" />
        <path d="m22 13-.82-.33c-.86-.34-1.82.2-1.98 1.11c-.11.7-.72 1.22-1.43 1.22H17" />
        <path d="m11 2 .33.82c.34.86-.2 1.82-1.11 1.98C9.52 4.9 9 5.52 9 6.23V7" />
        <path d="M11 13c1.93 1.93 2.83 4.17 2 5-.83.83-3.07-.07-5-2-1.93-1.93-2.83-4.17-2-5 .83-.83 3.07.07 5 2Z" />
      </>
    ),
  },
};

export function Reactions({ chore, me, reactBusyId, onReact }: {
  chore: ChoreItem;
  me: Session | null;
  reactBusyId: string | null;
  onReact: (chore: ChoreItem, glyph: string) => Promise<void>;
}) {
  /** The reaction strip + done-note receipt a card carries (card
   *  MnorjNDwB3of). Rendered on BOTH the open and done rows, because a
   *  reaction is warm on work in progress and on work finished. The glyph
   *  row is a closed set — there is no text field, so there is nothing to
   *  reply to and no thread can form. The receipt line shows who left it.
   *
   *  Both partners may pick the SAME glyph, so "is this mine" is answered by
   *  member, never by glyph — a `.find()` by glyph would return only the
   *  first entry and hide the second partner's own tap. Occupancy and my own
   *  selection are derived independently, and the count is shown so two
   *  reactions never masquerade as one (sol review 2026-10-16). */
  return (
      <div className="ch-social">
        <div className="ch-react" role="group" aria-label={`React to ${chore.title}`}>
          {CHORE_REACTION_GLYPHS.map(glyph => {
            const hits = chore.reactions.filter(r => r.glyph === glyph);
            const byMe = !!me && hits.some(r => r.memberId === me.memberId);
            // The wire still carries the emoji glyph (CHORE_REACTION_GLYPHS +
            // POST /react, unchanged); only the RENDER is a vector now, so the
            // strip reads as line art like the rest of the app and no emoji
            // codepoint ships in the DOM (card bFaRParNQH98). The shape also
            // supplies the chip's spoken name, so screen readers say "fire",
            // never "emoji fire". The glyph fallback exists only for a future
            // glyph added to the shared list without a shape here.
            const shape = REACTION_SHAPES[glyph];
            const spoken = shape?.label ?? glyph;
            return (
              <Pressable
                key={glyph}
                data-reaction={shape?.name}
                className={`ch-react-chip pear-tap-capsule${hits.length ? ' is-on' : ''}${byMe ? ' is-mine' : ''}`}
                disabled={reactBusyId === chore.choreId}
                aria-pressed={byMe}
                aria-label={`React ${spoken} to ${chore.title}${hits.length ? `, ${hits.length} reaction${hits.length === 1 ? '' : 's'}` : ''}`}
                onClick={() => void onReact(chore, glyph)}
              >
                {shape ? (
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    {shape.body}
                  </svg>
                ) : (
                  glyph
                )}
                {hits.length > 1 && <span className="ch-react-count">{hits.length}</span>}
              </Pressable>
            );
          })}
        </div>
        {chore.doneNote && (
          <p className="ch-receipt" data-testid="chore-receipt">
            <span className="ch-receipt-note">“{chore.doneNote}”</span>
            {noteByLabel(chore, me) && <span className="ch-receipt-by"> — {noteByLabel(chore, me)}</span>}
          </p>
        )}
      </div>
    );
}

export function NoteComposer({ chore, noteFor, noteDraft, onNoteDraft, busyId, onSaveNote, onClose }: {
  chore: ChoreItem;
  noteFor: string | null;
  noteDraft: string;
  onNoteDraft: (v: string) => void;
  busyId: string | null;
  onSaveNote: (chore: ChoreItem) => Promise<void>;
  onClose: () => void;
}) {
  if (noteFor !== chore.choreId) return null;
  /** The receipt composer itself (card MnorjNDwB3of). One line, one Save —
   *  a receipt, not a thread: there is no reply, no history, no @mention.
   *  On an open row the Save also ticks the chore off (a receipt needs a
   *  completion to receipt); on a done row it just replaces the line. */
    // The save rides the SAME per-row lock as Tick off (busyId) — the two
    // completion paths must exclude each other, so the spinner reads that
    // lock, not the reaction one (sol review 2026-10-16).
    const saving = busyId === chore.choreId;
    return (
      <div className="ch-note-composer" role="group" aria-label={`Add a note to ${chore.title}`}>
        <input
          className="pear-chat-input ch-note-input"
          data-testid="chore-note-input"
          value={noteDraft}
          maxLength={CHORE_DONE_NOTE_MAX}
          placeholder="done — out of bin liners"
          aria-label={`Done note for ${chore.title}`}
          onChange={e => onNoteDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') void onSaveNote(chore);
            if (e.key === 'Escape') onClose();
          }}
        />
        <Pressable
          className="pear-btn pear-btn-mustard ch-note-save"
          disabled={saving}
          aria-label={`Save note on ${chore.title}`}
          onClick={() => void onSaveNote(chore)}
        >
          {saving ? '…' : chore.status === 'open' ? 'Tick off + note' : 'Save note'}
        </Pressable>
        <Pressable
          className="pear-btn ch-note-cancel"
          disabled={saving}
          aria-label={`Cancel note on ${chore.title}`}
          onClick={onClose}
        >
          Cancel
        </Pressable>
      </div>
    );
}
